import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, X, Loader2, Type, Save } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@meno/ui/lib/utils'
import { Input } from '@meno/ui/components/ui/input'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import { Button } from '@meno/ui/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@meno/ui/components/ui/tabs'
import { uploadMedia, updateBrandAsset, getBrandAssets, BACKEND_URL } from '@meno/ui/lib/api'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { useTaskCenter } from '@/contexts/TaskCenterContext'
import { MediaCropModal } from '@meno/ui/components/shared/MediaCropModal'
import { ColorInput } from '@meno/ui/components/shared/ColorInput'

// ── Slot config ───────────────────────────────────────────────────────────────
type SlotKey =
  | 'logo'
  | 'logo_secondary'
  | 'favicon'
  | 'banner'
  | 'event_banner'
  | 'pattern_primary'
  | 'pattern_secondary'
  | 'watermark'
  | 'banner_phone'
  | 'event_banner_phone'

interface SlotDef {
  key: SlotKey
  span: string
  accept?: string
}

const GENERAL_SLOTS: SlotDef[] = [
  { key: 'logo',              span: 'col-span-2 row-span-2', accept: 'image/*' },
  { key: 'favicon',           span: 'col-span-1 row-span-1', accept: 'image/*' },
  { key: 'watermark',         span: 'col-span-1 row-span-1', accept: 'image/*' },
  { key: 'logo_secondary',    span: 'col-span-1 row-span-1', accept: 'image/*' },
  { key: 'pattern_primary',   span: 'col-span-2 row-span-2', accept: 'image/*' },
  { key: 'pattern_secondary', span: 'col-span-2 row-span-1', accept: 'image/*' },
]

const DESKTOP_SLOTS: SlotDef[] = [
  { key: 'banner',            span: 'col-span-4 row-span-2', accept: 'image/*' },
  { key: 'event_banner',      span: 'col-span-4 row-span-2', accept: 'image/*' },
]

const PHONE_SLOTS: SlotDef[] = [
  { key: 'banner_phone',      span: 'col-span-2 row-span-2', accept: 'image/*' },
  { key: 'event_banner_phone',span: 'col-span-2 row-span-2', accept: 'image/*' },
]

const SLOTS: SlotDef[] = [...GENERAL_SLOTS, ...DESKTOP_SLOTS, ...PHONE_SLOTS]

type AssetMap = Record<string, string | null>

function defaultRatioForSlot(key: SlotKey): number | null {
  if (['logo', 'logo_secondary', 'favicon'].includes(key)) return 1
  if (['banner', 'event_banner'].includes(key)) return 16 / 9
  if (['banner_phone', 'event_banner_phone'].includes(key)) return 9 / 16
  return null
}

// ── Image slot card ───────────────────────────────────────────────────────────
function AssetSlot({
  def, url, onUpload, onRemove,
}: {
  def: SlotDef
  url: string | null
  onUpload: (key: SlotKey, file: File) => Promise<void>
  onRemove: (key: SlotKey) => void
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  useEffect(() => { setImgFailed(false) }, [url])

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPendingFile(file)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleCropConfirm = async (cropped: File) => {
    setPendingFile(null)
    setUploading(true)
    try {
      await onUpload(def.key, cropped)
    } finally {
      setUploading(false)
    }
  }

  const rawResolved = url
    ? url.startsWith('http') ? url : `${BACKEND_URL}${url}`
    : null
  const resolvedUrl = imgFailed ? null : rawResolved

  return (
    <div
      className={cn(
        'relative group rounded-2xl border-2 border-dashed border-border bg-card overflow-hidden transition-all',
        'hover:border-primary/60 hover:shadow-md cursor-pointer',
        def.span,
      )}
      onClick={() => !uploading && inputRef.current?.click()}
    >
      {resolvedUrl && (
        <img
          src={resolvedUrl}
          alt={def.key}
          className="absolute inset-0 w-full h-full object-contain p-3"
          onError={() => setImgFailed(true)}
        />
      )}

      <div
        className={cn(
          'absolute inset-0 flex flex-col items-center justify-center gap-2 transition-opacity p-4',
          resolvedUrl ? 'opacity-0 group-hover:opacity-100 bg-black/50' : 'opacity-100',
        )}
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        ) : (
          <div className={cn('p-3 rounded-xl', resolvedUrl ? 'bg-white/20' : 'bg-muted')}>
            <Upload className={cn('w-5 h-5', resolvedUrl ? 'text-white' : 'text-muted-foreground')} />
          </div>
        )}
        <div className="text-center">
          <p className={cn('font-semibold text-sm leading-tight', resolvedUrl ? 'text-white' : 'text-foreground')}>
            {t(`brandAssets.${def.key}`)}
          </p>
          <p className={cn('text-[11px] mt-0.5 leading-snug', resolvedUrl ? 'text-white/80' : 'text-muted-foreground')}>
            {uploading ? t('brandAssets.uploadingAsset') : t('brandAssets.clickToUpload')}
          </p>
        </div>
      </div>

      {resolvedUrl && !uploading && (
        <button
          className="absolute top-2 end-2 z-10 p-1 rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 touch-reveal transition-opacity"
          onClick={e => { e.stopPropagation(); onRemove(def.key) }}
        >
          <X className="w-3 h-3" />
        </button>
      )}

      <input ref={inputRef} type="file" hidden accept={def.accept} onChange={handleFile} />

      <MediaCropModal
        file={pendingFile}
        onConfirm={handleCropConfirm}
        onCancel={() => setPendingFile(null)}
        defaultRatio={defaultRatioForSlot(def.key)}
      />
    </div>
  )
}

// ── Color row ─────────────────────────────────────────────────────────────────
function ColorRow({
  label, value, onChange,
}: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted-foreground w-28 shrink-0">{label}</span>
      <div className="flex-1">
        <ColorInput variant="inline" value={value} onChange={onChange} />
      </div>
    </div>
  )
}

// ── Spacing row ───────────────────────────────────────────────────────────────
function SpacingRow({
  label, value, onChange,
}: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted-foreground w-28 shrink-0">{label}</span>
      <div className="flex items-center gap-2 flex-1">
        <Input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="h-8 text-xs w-24"
          placeholder="0"
          min={0}
        />
        <span className="text-xs text-muted-foreground">px</span>
      </div>
    </div>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-card p-5">
      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-4">{title}</p>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function BrandAssetsPage() {
  const { t } = useTranslation()
  // Reuse the Asset page's title so the TopBar reads "Asset" on both the
  // Media tabs and the Brand Assets tab — they're conceptually the same
  // page with the tab strip rendered by AssetTabs.
  usePageHeader({ title: t('assets.title', 'Asset') })
  const { runUpload } = useTaskCenter()
  const fontInputRef = useRef<HTMLInputElement>(null)

  const [assets, setAssets] = useState<AssetMap>({})
  const [isLoading, setIsLoading] = useState(true)
  const [fontUploading, setFontUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    try {
      const r = await getBrandAssets()
      const map: AssetMap = {}
      for (const item of r.data as { slot_key: string; url: string | null }[]) {
        map[item.slot_key] = item.url
      }
      setAssets(map)
    } catch {
      // fall through, show empty state
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  // ── Image asset handlers ─────────────────────────────────────────────────────
  const handleUpload = async (key: SlotKey, file: File) => {
    try {
      const uploaded = await runUpload({
        label: file.name, kind: 'media',
        upload: (onProgress) => uploadMedia(file, onProgress),
      })
      const url: string = uploaded.data.url
      await updateBrandAsset(key, url)
      setAssets(prev => ({ ...prev, [key]: url }))
      toast.success(t('brandAssets.uploadSuccess', 'Brand asset uploaded'))
    } catch {
      toast.error(t('brandAssets.uploadError', 'Upload failed'))
      throw new Error('upload failed')
    }
  }

  const handleRemove = async (key: SlotKey) => {
    await updateBrandAsset(key, null)
    setAssets(prev => ({ ...prev, [key]: null }))
  }

  // ── Identity field handlers ──────────────────────────────────────────────────
  const saveSlot = async (key: string, value: string | null) => {
    try {
      await updateBrandAsset(key, value)
      setAssets(prev => ({ ...prev, [key]: value }))
    } catch {
      toast.error('Failed to save')
    }
  }

  const handleFontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFontUploading(true)
    try {
      const uploaded = await runUpload({
        label: file.name, kind: 'media',
        upload: (onProgress) => uploadMedia(file, onProgress),
      })
      await saveSlot('font_primary', uploaded.data.url)
      toast.success('Font uploaded')
    } catch {
      toast.error('Font upload failed')
    } finally {
      setFontUploading(false)
      if (fontInputRef.current) fontInputRef.current.value = ''
    }
  }

  const colorBg      = assets['color_bg']      ?? '#0a0a0a'
  const colorFg      = assets['color_fg']      ?? '#ffffff'
  const colorAccent  = assets['color_accent']  ?? '#5b8ef0'
  const fontPrimary  = assets['font_primary']  ?? null
  const spacingBase  = assets['spacing_base']  ?? '8'
  const spacingSection = assets['spacing_section'] ?? '24'
  const spacingItem  = assets['spacing_item']  ?? '16'

  const handleSaveAll = async () => {
    setSaving(true)
    try {
      await Promise.all([
        updateBrandAsset('color_bg', colorBg),
        updateBrandAsset('color_fg', colorFg),
        updateBrandAsset('color_accent', colorAccent),
        updateBrandAsset('spacing_base', spacingBase),
        updateBrandAsset('spacing_section', spacingSection),
        updateBrandAsset('spacing_item', spacingItem),
      ])
      toast.success('Brand assets saved')
    } catch {
      toast.error('Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{t('brandAssets.title')}</h2>
          <p className="text-muted-foreground text-sm mt-1">{t('brandAssets.subtitle')}</p>
        </div>
        <Button onClick={handleSaveAll} disabled={saving || isLoading} size="sm" className="gap-1.5">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] gap-3">
            <Skeleton className="col-span-2 row-span-2 rounded-2xl" />
            <Skeleton className="col-span-1 row-span-1 rounded-2xl" />
            <Skeleton className="col-span-1 row-span-1 rounded-2xl" />
            <Skeleton className="col-span-1 row-span-1 rounded-2xl" />
            <Skeleton className="col-span-2 row-span-2 rounded-2xl" />
            <Skeleton className="col-span-2 row-span-1 rounded-2xl" />
          </div>
        </div>
      ) : (
        <>
        {/* Full-width chrome strip — same bg as the TopBar. See UsersPage /
            ScreenDetailsPage for the canonical pattern. */}
        <Tabs defaultValue="fonts-spacing">
          <div
            className="mb-6 border-b border-border brand-chrome-light"
            style={{ marginInline: 'calc(-1 * var(--space-l))' }}
          >
            <div className="px-fluid-l">
              <TabsList className="h-11 bg-transparent p-0 rounded-none gap-1">
                <TabsTrigger
                  value="fonts-spacing"
                  className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  Fonts & Spacing Rules
                </TabsTrigger>
                <TabsTrigger
                  value="colors"
                  className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  Colors
                </TabsTrigger>
                <TabsTrigger
                  value="media"
                  className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                >
                  Media
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent value="fonts-spacing" className="space-y-6 mt-0">
            {/* Fonts */}
            <Section title="Fonts">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'flex items-center gap-3 flex-1 rounded-xl border border-dashed border-border bg-muted p-3 cursor-pointer',
                    'hover:border-primary/60 transition-colors group',
                  )}
                  onClick={() => !fontUploading && fontInputRef.current?.click()}
                >
                  <div className="p-2 rounded-lg bg-background border border-border">
                    {fontUploading
                      ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      : <Type className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {fontPrimary ? 'Primary font uploaded' : 'Upload primary font'}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {fontPrimary
                        ? fontPrimary.split('/').pop()
                        : 'Supports .ttf, .otf, .woff, .woff2'
                      }
                    </p>
                  </div>
                  <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                </div>
                {fontPrimary && (
                  <button
                    onClick={() => saveSlot('font_primary', null)}
                    className="p-1.5 rounded-full border border-border bg-background text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <input
                ref={fontInputRef}
                type="file"
                hidden
                accept=".ttf,.otf,.woff,.woff2,font/*"
                onChange={handleFontUpload}
              />
            </Section>

            {/* Spacing Rules */}
            <Section title="Spacing Rules">
              <SpacingRow
                label="Base unit"
                value={spacingBase}
                onChange={v => setAssets(prev => ({ ...prev, spacing_base: v }))}
              />
              <SpacingRow
                label="Section gap"
                value={spacingSection}
                onChange={v => setAssets(prev => ({ ...prev, spacing_section: v }))}
              />
              <SpacingRow
                label="Item padding"
                value={spacingItem}
                onChange={v => setAssets(prev => ({ ...prev, spacing_item: v }))}
              />
            </Section>
          </TabsContent>

          <TabsContent value="colors" className="space-y-6 mt-0">
            {/* Colors */}
            <Section title="Colors">
              <ColorRow
                label="Background"
                value={colorBg}
                onChange={v => setAssets(prev => ({ ...prev, color_bg: v }))}
              />
              <ColorRow
                label="Foreground"
                value={colorFg}
                onChange={v => setAssets(prev => ({ ...prev, color_fg: v }))}
              />
              <ColorRow
                label="Accent"
                value={colorAccent}
                onChange={v => setAssets(prev => ({ ...prev, color_accent: v }))}
              />
            </Section>
          </TabsContent>

          <TabsContent value="media" className="space-y-6 mt-0">
            {/* Asset uploads grid */}
            <Section title="General Media">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] gap-3">
                {GENERAL_SLOTS.map(def => (
                  <AssetSlot
                    key={def.key}
                    def={def}
                    url={assets[def.key] ?? null}
                    onUpload={handleUpload}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </Section>

            <Section title="Desktop Sizes">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] gap-3">
                {DESKTOP_SLOTS.map(def => (
                  <AssetSlot
                    key={def.key}
                    def={def}
                    url={assets[def.key] ?? null}
                    onUpload={handleUpload}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </Section>

            <Section title="Phone Sizes">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] gap-3">
                {PHONE_SLOTS.map(def => (
                  <AssetSlot
                    key={def.key}
                    def={def}
                    url={assets[def.key] ?? null}
                    onUpload={handleUpload}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </Section>
          </TabsContent>
        </Tabs>
        </>
      )}
    </div>
  )
}
